/**
 * Standard terminology constants for DiceCapture UI
 *
 * Maintains consistent language across the application.
 * Use these constants instead of hardcoded strings in user-facing text.
 */

/**
 * Connection action terminology
 */
export const CONNECTION_ACTIONS = {
  CONNECT: 'Connect',
  DISCONNECT: 'Disconnect',
  RETRY: 'Retry',
  RECONNECTING: 'Reconnecting'
} as const;

/**
 * Connection status terminology
 */
export const CONNECTION_STATUS = {
  CONNECTED: 'Connected',
  DISCONNECTED: 'Disconnected',
  CONNECTING: 'Connecting',
  ERROR: 'Connection Failed'
} as const;

/**
 * Page and section titles
 */
export const PAGE_TITLES = {
  HOME: 'Sessions',
  LOGIN: 'Sign In',
  ENDPOINTS: 'Connections',
  SETTINGS: 'Settings',
  PRACTICE: 'Practice',
  ROLL: 'Roll Dice',
  REGISTRATION: 'Register Dice',
  ERROR_LOG: 'Error Log'
} as const;

/**
 * Form field labels
 */
export const FORM_LABELS = {
  ENDPOINT_NAME: 'Connection Name',
  SERVER_URL: 'Server URL',
  ROOM_CODE: 'Session Code',
  PLAYER_NAME: 'Player Name',
  ENDPOINT_TYPE: 'Connection Type',
  DISCORD_SERVER_NAME: 'Guild Name',
  DISCORD_SERVER_ID: 'Guild ID',
  DISCORD_CHANNEL_NAME: 'Channel Name',
  DISCORD_CHANNEL_ID: 'Channel ID'
} as const;

/**
 * Button and action text
 */
export const ACTIONS = {
  ADD_ENDPOINT: 'Add Connection',
  EDIT_ENDPOINT: 'Edit Connection',
  DELETE_ENDPOINT: 'Remove Connection',
  SAVE_ENDPOINT: 'Save Connection',
  START_SESSION: 'Start Session',
  JOIN_SESSION: 'Join Session',
  SIGN_IN: 'Sign In',
  SIGN_OUT: 'Sign Out',
  CHANGE_PASSWORD: 'Change Password',
  CREATE_ACCOUNT: 'Create Account',
  CANCEL: 'Cancel',
  ROLL_DICE: 'Roll Dice',
  START_CAMERA: 'Start Camera',
  STOP_CAMERA: 'Stop Camera'
} as const;

/**
 * Empty state messages
 */
export const EMPTY_STATES = {
  NO_ENDPOINTS: 'No connections configured',
  NO_ENDPOINTS_CTA: 'Add your first connection to begin',
  NO_ACTIVE_CONNECTIONS: 'No active connections',
  NO_SESSIONS: 'No sessions yet',
  NO_SESSIONS_CTA: 'Start a new session or join an existing one to begin capturing dice rolls.'
} as const;

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
  CONNECTION_FAILED: 'Connection failed',
  MAX_CONNECTIONS: 'Cannot connect: Maximum 3 connections allowed',
  CAMERA_FAILED: 'Unable to access camera',
  RECOGNITION_FAILED: 'Recognition failed',
  GENERAL_ERROR: 'Something went wrong'
} as const;

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = {
  ENDPOINT_CREATED: 'Connection created successfully',
  ENDPOINT_UPDATED: 'Connection updated successfully',
  ENDPOINT_DELETED: 'Connection removed successfully',
  SESSION_CREATED: 'Session created successfully',
  SESSION_JOINED: 'Joined session successfully',
  SESSION_DELETED: 'Session removed successfully',
  CONNECTION_ESTABLISHED: 'Connected successfully',
  CONNECTION_CLOSED: 'Disconnected successfully'
} as const;

/**
 * Accessibility labels
 */
export const ARIA_LABELS = {
  ADD_ENDPOINT_BUTTON: 'Add new connection',
  CONNECT_BUTTON: 'Connect',
  DISCONNECT_BUTTON: 'Disconnect',
  EDIT_ENDPOINT_BUTTON: 'Edit connection configuration',
  DELETE_ENDPOINT_BUTTON: 'Delete connection',
  ENDPOINT_CARD: 'Connection card',
  CONNECTION_STATUS_BADGE: 'Connection status indicator'
} as const;

/**
 * Loading states
 */
export const LOADING_STATES = {
  CONNECTING: 'Connecting...',
  LOADING: 'Loading...',
  PROCESSING: 'Processing...'
} as const;
